<template>
  <div v-if="open" id="controlsContent" @click="leaveOnClick($event)">
    <div ref="wrapper" class="controls-content__wrapper">
      <header class="controls-content__header">
        <div class="controls-content__title">{{ $lng(title) }}</div>
        <div class="controls-content__close-btn" @click="open = null" />
      </header>
      <main>
        <div v-if="content === 'chart-settings'" class="controls-content__settings">
          <div class="controls-content__settings-body">
            <section>
              <div class="controls-content__subtitle">{{ $lng('colorBlindness') }}</div>
              <figure
                class="palette normal"
                :class="{ active: palette === 'normal' }"
                @click="palette = 'normal'"
              >
                <img
                  :src="thumbnail('normal-palette')"
                  alt="Normal pallete"
                  width="100%"
                >
                <figcaption>Default</figcaption>
              </figure>
              <figure
                class="palette tritanopia"
                :class="{ active: palette === 'tritanopia' }"
                @click="palette = 'tritanopia'"
              >
                <img
                  :src="thumbnail('tritanopia-palette')"
                  alt="Normal pallete"
                  width="100%"
                >
                <figcaption>Grey</figcaption>
              </figure>
            </section>
            <section>
              <div class="controls-content__subtitle">{{ $lng('textSize') }}</div>
              <div class="text-size-selector">
                <select v-model="fontSize">
                  <!-- <option value="14">Default (14px)</option> -->
                  <option v-for="(opt, i) in fontSizeOptions" :key="i" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>
            </section>
          </div>
        </div>
        <div v-else-if="content === 'chart-disclaimer'" class="controls-content__disclaimer">
          <vue-scroll :ops="{ bar: { background: '#4283e366' } }">
            <div class="disclaimer-content">
              <section v-for="(note) in disclaimers" :key="note.id">
                <p class="disclaimer-content__content" v-html="note.value"></p>
              </section>
            </div>
          </vue-scroll>
        </div>
        <div v-else-if="content === 'chart-download'" class="controls-content__download">
          <section>
            <div class="controls-content__subtitle">Data formats</div>
            <div class="controls-content__icons-list">
              <div id="csv" class="controls-content__icons-item" @click="downloadAs('csv')">
                <div class="controls-content__icons__icon csv" />
                <div class="controls-content__icons__text">CSV</div>
              </div>
              <div id="xlsx" class="controls-content__icons-item" @click="downloadAs('xlsx')">
                <div class="controls-content__icons__icon xlsx" />
                <div class="controls-content__icons__text">XLSX</div>
              </div>
            </div>
          </section>
          <section>
            <div class="controls-content__subtitle">Image formats</div>
            <div class="controls-content__icons-list">
              <div id="png" class="controls-content__icons-item" @click="downloadAs('png')">
                <div class="controls-content__icons__icon png" />
                <div class="controls-content__icons__text">PNG</div>
              </div>
            </div>
          </section>
        </div>
        <div v-else class="controls-content__share">
          <section class="clipboards">
            <button
              class="controls-content__clipboard-link button"
              :data-clip="encodeURIComponent(embeddedCode)"
              @click="storeInClipBoard"
            >
              Embedded Code
            </button>
            <button
              class="controls-content__clipboard-link button"
              :data-clip="encodeURIComponent(shareLink)"
              @click="storeInClipBoard"
            >
              Share Link
            </button>
          </section>
          <section class="social">
            <div class="controls-content__icons-list">
              <ShareNetwork
                class="controls-content__icons-item"
                network="twitter"
                :url="shareLink"
                title="OECD Data Explorer"
                description="OECD Data Explorer description"
                hashtags="OECD"
                inline-templates
              >
                <div class="controls-content__icons__icon twitter-x" />
                <div class="controls-content__icons__text">X</div>
              </ShareNetwork>
              <ShareNetwork
                class="controls-content__icons-item"
                network="facebook"
                :url="shareLink"
                title="OECD Data Explorer"
                description="OECD Data Explorer description"
                hashtags="OECD"
                inline-templates
              >
                <div class="controls-content__icons__icon facebook" />
                <div class="controls-content__icons__text">Facebook</div>
              </ShareNetwork>
              <ShareNetwork
                class="controls-content__icons-item"
                network="email"
                :url="shareLink"
                title="OECD Data Explorer"
                description="OECD Data Explorer description"
                hashtags="OECD"
                inline-templates
              >
                <div class="controls-content__icons__icon email" />
                <div class="controls-content__icons__text">Mail</div>
              </ShareNetwork>
            </div>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import DisclaimersMixin from '@/mixins/disclaimers.js'
import { encodeDate } from '@/utils/dates.js'

export default {
  mixins: [DisclaimersMixin],
  model: {
    prop: 'content',
    event: 'close',
  },
  props: ['content', 'mode', 'chartData'],
  data() {
    return {
      settingsTabs: ['colorBlindness', 'textSize'],
      settingsActiveTab: 'colorBlindness',
    }
  },
  computed: {
    open: {
      get() {
        return this.content != null
      },
      set() {
        this.$emit('close', null)
      },
    },
    title() {
      return this.content
    },
    shareLink() {
      return location.href
    },
    embeddedCode() {
      const embeddedURL = location.href.replace(/\/(explore|print|thumbnail)\//, '/embedded/')
      return `<iframe width="900" height="600" src="${embeddedURL}" frameborder="0"></iframe>`
    },
    fontSizeOptions() {
      return this.isMobile
        ? [
            {
              label: 'Default',
              value: 11,
            },
            {
              label: 'Large',
              value: 14,
            },
          ]
        : [
            {
              label: 'Default',
              value: 12,
            },
            {
              label: 'Large',
              value: 16,
            },
          ]
    },
    fontSize: {
      get() {
        return this.$store.state.query.fontSize
      },
      set(to) {
        this.$store.commit('fontSize', to)
      },
    },
    palette: {
      get() {
        return this.$store.state.query.palette
      },
      set(to) {
        return this.$store.commit('palette', to)
      },
    },
    thumbnail: () => imageName => {
      return require(`@/assets/images/${imageName}-thumb.png`)
    },
  },
  methods: {
    storeInClipBoard(ev) {
      const link = ev.target
      navigator.clipboard.writeText(decodeURIComponent(ev.target.dataset.clip))
      const popupAnchor = document.createElement('span')
      popupAnchor.setAttribute('class', 'clipboard-popup__anchor')
      popupAnchor.innerHTML = `<div class="clipboard-popup">
        <div class="clipboard-popup__content">Copied to the clipboard</div>
      </div>`
      link.appendChild(popupAnchor)
      setTimeout(() => popupAnchor.children[0].classList.add('visible'), 0)
      setTimeout(() => {
        link.removeChild(popupAnchor)
      }, 15e2)
    },
    filterDataToDownload(slot) {
      const data = this.chartData
      const filter = data.slice(1).reduce((acum, row) => {
        const iso = row[data.fields._COUNTRY_].replace(/--.*/, '')
        acum[iso] = acum[iso] || new Set()
        const date = encodeDate(row[data.fields._DATE_])
        if (date !== null) acum[iso].add(date)
        return acum
      }, {})
      const tableData = this.$store.getters[slot + 'DataAsTable']

      const headers = tableData[0].types.reduce((acum, d, i) => {
        acum[d] = i
        return acum
      }, {})
      return [tableData[0]].concat(
        tableData.slice(1).filter(row => {
          return filter[row[headers.iso]] && filter[row[headers.iso]].has(row[headers.time])
        })
      )
    },
    downloadAs(format) {
      const button = this.$el.querySelector('#' + format)
      if (
        button.classList.contains('error') ||
        button.classList.contains('disabled') ||
        button.classList.contains('loading')
      )
        return
      button.classList.add('loading')
      try {
        let mainData = null,
          pairData = null,
          sizeData = null
        switch (format) {
          case 'csv':
            mainData = this.filterDataToDownload('main')
            if (this.mode === 'compare') {
              pairData = this.filterDataToDownload('pair')
            }
            if (this.$store.state.data.size) {
              sizeData = this.filterDataToDownload('size').slice(1)
            }
            this.$store
              .dispatch('exportCSV', {
                disclaimers: this.disclaimers,
                mode: this.mode,
                mainData: mainData,
                pairData: pairData,
                sizeData: sizeData,
              })
              .then(blob => {
                this.exportFile('oecd-data-kitchen.zip', URL.createObjectURL(blob))
                button.classList.remove('loading')
              })
            break
          case 'xlsx':
            mainData = this.filterDataToDownload('main')
            if (this.mode === 'compare') {
              pairData = this.filterDataToDownload('pair')
            }
            if (this.$store.state.data.size) {
              sizeData = this.filterDataToDownload('size').slice(1)
            }
            this.$store
              .dispatch('exportXLSX', {
                disclaimers: this.disclaimers,
                mode: this.mode,
                mainData: mainData,
                pairData: pairData,
                sizeData: sizeData,
              })
              .then(blob => {
                this.exportFile('oecd-data-kitchen.zip', URL.createObjectURL(blob))
                button.classList.remove('loading')
              })
            break
          case 'png':
            this.$store
              .dispatch('exportPNG', {
                url: location.href,
                disclaimers: this.disclaimers,
              })
              .then(blob => {
                this.exportFile('oecd-data-kitchen.zip', URL.createObjectURL(blob))
                URL.revokeObjectURL(blob)
                button.classList.remove('loading')
              })
              .catch(() => {
                button.classList.remove('loading')
                button.classList.add('error')
              })
            break
          case 'svg':
            this.exportSVG()
              .then(blob => {
                this.exportFile('oecd-data-kitchen.zip', URL.createObjectURL(blob))
                button.classList.remove('loading')
              })
              .catch(err => {
                console.error(err)
                button.classList.remove('loading')
                button.classList.add('error')
              })
            break
        }
      } catch (err) {
        button.classList.remove('loading')
        button.classList.add('error')
      }
    },
    leaveOnClick(ev) {
      if (
        this.$refs.wrapper &&
        !this.$refs.wrapper.contains(ev.target) &&
        this.$refs.wrapper !== ev.target
      ) {
        this.open = null
      }
    },
    exportSVG() {
      return new Promise(resolve => {
        this.$nextTick(() => {
          return window.currentChart.exportSVG().then(source => {
            this.$store
              .dispatch('exportSVG', {
                source: source,
                disclaimers: this.disclaimers,
              })
              .then(resolve)
            // resolve(self.exportFile('oecd-data-kitchen.svg', url))
          })
        })
      })
    },
    exportFile(fileName, url) {
      let link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style lang="stylus">
#controlsContent
  align-items: center
  bottom: 0
  display: flex
  height: 100vh
  justify-content: flex-end
  left: 0
  padding-right: 2em
  position: fixed
  top: 0
  width: 100vw
  z-index: 1000

  .mobile &
    padding: 0

    &:before
      background-color: rgba(#000, 0.5)
      content: ""
      height: 100%
      position: fixed
      width: 100%

.controls-content__wrapper
  background-color: #fff
  border-radius: 5px
  box-shadow: 1px 3px 10px 4px rgba(0, 0, 0, 0.05)
  margin-left: auto
  padding: 1.2em 1.5em
  width: vw(300px)
  max-height: 80%;
  overflow-y: auto

  .mobile &
    left: 50%
    margin: 0
    max-width: 90%
    position: absolute
    top: 50%
    transform: translate(-50%, -50%)
    width: 100%

    +m-breakpoint(md)
      max-width: 50%

  section
    &:not(:last-child)
      margin-bottom: vw(20px)

      .mobile &
        +m-breakpoint(md)
          margin-bottom: vh(20px)

.controls-content__header
  m-font-size(16, 20)
  align-items: center
  display: flex
  font-weight: 600
  margin-bottom: vw(20px)

  .mobile &
    +m-breakpoint(md)
      margin-bottom: vh(20px)
      font-size: .9em

.controls-content__title
  color: $black
  flex: 1 1 0%
  margin-right: vw(15px)

.controls-content__subtitle
  color: $black
  font-weight: 600
  margin-bottom: vw(10px)
  font-size: .8em

.controls-content__close-btn
  m-icon("close", 30)
  cursor: pointer

  .mobile &
    +m-breakpoint(md)
      m-icon("close", 15)

.controls-content__settings-tab
  cursor: pointer
  display: inline-block
  margin: 0.3em 0.8em
  padding: 0.4em 0.2em

  &.active
    border-bottom: 2px solid
    font-weight: bold

  &:last-child
    margin-right: 5em

.controls-content__settings-body
  padding: 0.5em 0em

  .controls-content__settings-title
    color: $black
    margin-bottom: .25em

    .mobile &
      font-size: .85em

  select
    width: 100%
    padding: .25em .45em
    border-radius: 5px
    border: 1px solid #ddd
    background: none
    color: $black
    font-size: .85em

  figure
    border-radius: 5px
    cursor: pointer
    display: inline-block
    margin: 0
    margin-right: 0.5em
    padding: 0.75em
    width: calc(50% - 0.25em)

    &.active
      border: 1px solid #00bbff

    &:last-child
      margin-right: 0

    &:not(.active) img
      filter: grayscale(80%)

  figcaption
    text-align: center

    .mobile &
      font-size: .85em

.controls-content__label-switcher
  margin: 0

.controls-content__disclaimer

  .disclaimer-content__title
    m-font-size(16, 20)
    font-weight: 600
    margin-bottom: vw(20px)
    margin-top: 0

  .disclaimer-content__content
    m-font-size(12, 16)
    padding-right: 2em
    word-break: break-word
    a
      color: $blue
      text-decoration: underline

    .mobile &
      font-size: 0.65em
      line-height: 1.3

.controls-content__download
  padding: 0.5em 0

.controls-content__icons-list
  display: grid
  grid-gap: 20px
  grid-template-columns: auto auto auto

.controls-content__icons-item
  &.loading,
  &.disabled
    opacity: 0.5

    .controls-content__icons__icon
      cursor: default

  &.loading
    .controls-content__icons__icon
      background-image: unset
      cursor: default
      m-spinner(1px, $blue)

  &.error
    color: red
    $icons = "csv", "xlsx", "png", "svg"

    for $icon in $icons
      .controls-content__icons__icon.{$icon}
        cursor: default
        m-icon(s("%s--error", convert($icon)), 30)

.controls-content__icons__icon
  background-position: center
  background-repeat: no-repeat
  background-size: 50% 50%
  cursor: pointer
  height: vw(40px)
  margin: 0 auto .25em
  width: vw(40px)

  $icons = "csv", "xlsx", "png", "svg", "facebook", "twitter-x", "email"

  for $icon in $icons
    &.{$icon}
      m-icon($icon, 30)

      .mobile &
        m-icon($icon, 35)

        +m-breakpoint(md)
          m-icon($icon, 20)

.controls-content__icons__text
  m-font-size(12, 14)
  text-align: center

  .mobile &
    font-size: .8em

.controls-content__share
  section.clipboards
    display: flex
    flex-direction: column

  .controls-content__clipboard-link
    display: block
    position: relative
    border-radius: 5px
    padding-right: "calc(2em + %s)" % vw(20px)
    background-color: $blue-2
    border-color: $blue-2

    + .controls-content__clipboard-link
      margin-left: 0 !important
      margin-top: .5em

    .mobile &
      font-size: 0.65em
      line-height: 1.32

      &:after
        m-icon("clipboard", 20)

        +m-breakpoint(md)
          m-icon("clipboard", 10)

    &:after
      content: ""
      position: absolute
      right: 1.5em
      top: 48%
      transform: translateY(-50%)
      width: vw(20px)
      height: @width
      m-icon("clipboard", 20)

    .clipboard-popup__anchor
      position: relative

    .clipboard-popup
      position: absolute
      top: 0
      left: 0
      transform: translate(-50%, -50%)
      transition: top 200ms ease-in-out

      &.visible
        top: -1em

    .clipboard-popup__content
      padding: .2em .6em
      background-color: #a5a5a5
      color: white
      border-radius: 5px
      white-space: nowrap
</style>
